// Review Center -> Markups (owner, 2026-09-18): the colour-marked install
// trackers, traced by root-04 and mapped onto the host model's elements
// (back/utils/markupProposals). Nothing is a tracking status until it is
// approved here; approving never downgrades an element already installed.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Space, Table, Tag, Tooltip, message } from "antd";
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const SWATCH = { blue: "#1e6fd9", orange: "#f28c28", green: "#2e9e44" };
const STATUS = { being_installed: "Being installed", installed: "Installed" };

async function api(path, opts = {}) {
  const res = await fetch(`${url}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || "Request failed");
  return body;
}

function Proposal({ projectId, id, canDecide, onChanged, sheetName }) {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api(`/projects/${projectId}/markup-proposals/${id}`).then((b) => setP(b.proposal)).catch((e) => message.error(e.message)), [projectId, id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  const act = async (action, ids) => {
    setBusy(action);
    try {
      const r = await api(`/projects/${projectId}/markup-proposals/${id}/decide`, { method: "POST", body: JSON.stringify({ segmentIds: ids, action }) });
      message.success(action === "approve" ? `${r.applied} element statuses applied${r.skipped ? `, ${r.skipped} already further along` : ""}` : `${r.decided} segment(s) rejected`);
      setSel([]); await load(); onChanged && onChanged();
    } catch (e) { message.error(e.message); } finally { setBusy(""); }
  };
  const open = p.segments.filter((s) => !s.decision && s.elements && s.elements.length);
  const columns = [
    { title: "", dataIndex: "colour", width: 34, render: (c) => <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, background: SWATCH[c] || "#999" }} /> },
    { title: "Mark", dataIndex: "stage", render: (s, r) => <span>{s}<div style={{ fontSize: 11, color: "#888" }}>{r.kind === "equipment" ? "equipment" : `run${r.lenFt ? `, ${Math.round(r.lenFt)} ft` : ""}`}{r.cols && r.cols.length ? ` - col ${r.cols.join(", ")}` : ""}</div></span> },
    { title: "Proposed status", dataIndex: "status", width: 140, render: (s) => <Tag color={s === "installed" ? "green" : "blue"}>{STATUS[s] || s || "-"}</Tag> },
    { title: "Elements", width: 220, render: (_, r) => (r.elements && r.elements.length
      ? <Tooltip title={r.elements.slice(0, 12).map((e) => `${e.category} ${e.uniqueId.slice(-8)} (${e.distFt} ft)`).join(" | ")}><span>{r.elements.length} ({[...new Set(r.elements.map((e) => e.category))].join(", ")})</span></Tooltip>
      : <span style={{ color: "#c62828" }}>{r.reason || "none"}</span>) },
    { title: "", width: 110, render: (_, r) => (r.decision ? <Tag color={r.decision === "approved" ? "green" : "default"}>{r.decision}</Tag> : null) },
  ];
  return (
    <div style={{ border: "1px solid #EFEFEF", borderRadius: 8, padding: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <b>{sheetName || "Traced sheet"}</b>
        <span style={{ fontSize: 12, color: "#888" }}>traced {p.tracedAt ? new Date(p.tracedAt).toLocaleString() : "-"} · placed by {p.placement && p.placement.source} · {p.summary && p.summary.withElements}/{p.summary && p.summary.segments} marks matched · {p.summary && p.summary.elements} elements (host model)</span>
        {canDecide && (
          <Space style={{ marginLeft: "auto" }}>
            <Button size="small" disabled={!sel.length} loading={busy === "reject"} onClick={() => act("reject", sel)}>Reject selected</Button>
            <Button size="small" type="primary" disabled={!sel.length} loading={busy === "approve"} onClick={() => act("approve", sel)}>Approve selected</Button>
            <Button size="small" type="primary" ghost disabled={!open.length} loading={busy === "approve"} onClick={() => act("approve", open.map((s) => s.id))}>Approve all matched ({open.length})</Button>
          </Space>
        )}
      </div>
      <Table size="small" rowKey="id" pagination={{ pageSize: 25, size: "small" }} dataSource={p.segments} columns={columns}
        rowSelection={canDecide ? { selectedRowKeys: sel, onChange: setSel, getCheckboxProps: (r) => ({ disabled: !!r.decision || !(r.elements && r.elements.length) }) } : undefined} />
    </div>
  );
}

export default function MarkupsTab({ projectId, reloadKey, onCountChange }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!projectId) { setData(null); return; }
    api(`/projects/${projectId}/markup-proposals`).then((b) => {
      setData(b);
      if (onCountChange) onCountChange(b.proposals.reduce((n, p) => n + ((p.state === "pending") ? 1 : 0), 0));
    }).catch(() => setData({ proposals: [], traces: [] }));
  }, [projectId, onCountChange]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!projectId) return <Empty description="Choose a project" />;
  if (!data) return null;
  const unmapped = data.traces.filter((t) => !t.proposalId || t.stale);
  const map = async (traceId) => {
    try { await api(`/projects/${projectId}/markup-proposals/build`, { method: "POST", body: JSON.stringify({ traceId }) }); message.info("Mapping the marks onto the model - this can take a few minutes the first time"); setTimeout(load, 15000); } catch (e) { message.error(e.message); }
  };
  return (
    <div>
      {unmapped.length > 0 && (
        <div style={{ marginBottom: 12, padding: 10, background: "#F3F7F9", borderRadius: 6 }}>
          {unmapped.map((t) => (
            <div key={t._id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
              <span>{t.sheetName || "Traced sheet"} - {t.segments} marks{t.stale ? " (traced again since it was mapped)" : ""}</span>
              {data.canDecide && <Button size="small" onClick={() => map(t._id)}>Map onto the model</Button>}
              <a href={`/align/${projectId}?docId=${t.docId}&page=${t.page || 1}`}>Align this sheet</a>
            </div>
          ))}
        </div>
      )}
      {data.proposals.filter((p) => p.state === "needs-alignment").map((p) => (
        <div key={p._id} style={{ marginBottom: 10, color: "#8A6112" }}>
          {p.sheetName}: the sheet is not placed on the model yet - <a href={`/align/${projectId}?docId=${p.docId}&page=${p.page || 1}`}>align it</a>, then map again.
        </div>
      ))}
      {data.proposals.filter((p) => p.state !== "needs-alignment").map((p) => (
        <Proposal key={p._id} projectId={projectId} id={p._id} canDecide={data.canDecide} onChanged={load} sheetName={p.sheetName} />
      ))}
      {!data.proposals.length && !unmapped.length && <Empty description="No traced markups on this project" />}
    </div>
  );
}
